import type { CSSProperties, FC, ReactNode } from 'react'
import type { TrackItem } from '@/types'

import { useRef } from 'react'
import { ArrowUpRight, Play, Youtube } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
    fromToEach,
    gsap,
    MOTION_QUERIES,
    primeOnRefresh,
    ScrollTrigger,
    useGSAP
} from '@/lib/motion'
import SectionHeading from '@/components/landing/section-heading'
import MediaImage from '@/components/landing/media-image'
import TrackJacket from '@/components/landing/track-jacket'
import Sparkle from '@/components/landing/decor/sparkle'
import { MUSIC_COPY } from '@/constants/content'
import { siteConfig } from '@/constants/site'

interface MusicSectionProps {
    tracks: Array<TrackItem>
}

interface JacketLinkProps {
    track: TrackItem
    children: ReactNode
}

const LISTEN_LINK_CLASS = 'inline-flex w-fit items-center gap-1.5 border-b border-ink/40 pb-0.5 text-sm tracking-[0.14em] transition-colors duration-500 hover:border-gold hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'

// The whole jacket opens the song on YouTube when it has a listening link.
const JacketLink: FC<JacketLinkProps> = ({ track, children }): ReactNode => {
    if (!track.listenUrl) return <div className='group/jacket'>{children}</div>

    return (
        <a
            href={track.listenUrl}
            target='_blank'
            rel='noopener noreferrer'
            aria-label={`${track.title} を YouTube で聴く（新しいタブで開きます）`}
            className='group/jacket block rounded-[6px] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold'
        >
            {children}
        </a>
    )
}

const GATHER_STARS = [
    { x: 8, y: 18, size: 18, gold: true },
    { x: 18, y: 64, size: 12, gold: false },
    { x: 26, y: 12, size: 10, gold: true },
    { x: 34, y: 82, size: 16, gold: true },
    { x: 12, y: 42, size: 9, gold: false },
    { x: 44, y: 8, size: 13, gold: false },
    { x: 52, y: 90, size: 10, gold: true },
    { x: 61, y: 14, size: 20, gold: true },
    { x: 70, y: 76, size: 11, gold: false },
    { x: 78, y: 24, size: 14, gold: true },
    { x: 86, y: 58, size: 18, gold: true },
    { x: 92, y: 12, size: 9, gold: false },
    { x: 95, y: 84, size: 12, gold: true },
    { x: 4, y: 88, size: 10, gold: true },
    { x: 66, y: 46, size: 8, gold: false },
    { x: 24, y: 36, size: 8, gold: true },
    { x: 82, y: 92, size: 9, gold: false },
    { x: 40, y: 58, size: 7, gold: true }
]

const pad = (value: number): string => String(value).padStart(2, '0')

// MUSIC: ✦ stars drift into the centre of the screen and burst, then the jackets travel sideways
// while the visitor keeps scrolling down. The jacket in the middle comes forward and its title fades in.
// Phones (and reduced motion) get a native swipe carousel instead.
const MusicSection: FC<MusicSectionProps> = ({ tracks }): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)

    useGSAP(() => {
        const section = sectionRef.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        mm.add(MOTION_QUERIES.desktop, () => {
            section.setAttribute('data-enhanced', '')

            const track = q('[data-music="track"]')[0]
            const gather = q('[data-music="gather"]')[0]

            if (!track || !gather) return () => section.removeAttribute('data-enhanced')

            const jackets = q('[data-jacket]')
            const arts = q('[data-jacket-art]')
            const captions = q('[data-jacket-caption]')
            const counter = q('[data-music-counter]')[0]
            const bar = q('[data-music-bar]')[0]
            const stars = q('[data-gather-star]')

            const gatherLength = (): number => window.innerHeight * 1.1
            const distance = (): number => Math.max(0, track.offsetWidth - window.innerWidth)

            const setHeight = (): void => {
                section.style.height = `${Math.round(window.innerHeight + gatherLength() + distance())}px`
            }

            setHeight()
            ScrollTrigger.addEventListener('refreshInit', setHeight)

            const offsetToCenter = (element: HTMLElement): { x: number, y: number } => ({
                x: gather.offsetWidth / 2 - (element.offsetLeft + element.offsetWidth / 2),
                y: gather.offsetHeight / 2 - (element.offsetTop + element.offsetHeight / 2)
            })

            const gatherTimeline = gsap.timeline({
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: () => `+=${gatherLength()}`,
                    scrub: 1,
                    invalidateOnRefresh: true,
                    onRefresh: primeOnRefresh
                }
            })

            // One tween per star and per letter (see fromToEach), so a refresh never shows them early.
            fromToEach(gatherTimeline, stars, { autoAlpha: 0.95, x: 0, y: 0, scale: 1 }, {
                x: (_index: number, element: HTMLElement) => offsetToCenter(element).x,
                y: (_index: number, element: HTMLElement) => offsetToCenter(element).y,
                scale: 0.35,
                duration: 0.6,
                ease: 'power2.in'
            }, 0, 0.012)

            gatherTimeline
                .to(stars, { autoAlpha: 0, duration: 0.12 }, 0.64 + stars.length * 0.012)
                .fromTo(q('[data-music="flash"]'), { autoAlpha: 0, scale: 0.15 }, { autoAlpha: 1, scale: 1, duration: 0.14, ease: 'power2.out' }, 0.56)
                .to(q('[data-music="flash"]'), { autoAlpha: 0, scale: 2.6, duration: 0.32, ease: 'power2.out' }, 0.72)
                .fromTo(q('[data-music="content"]'), { autoAlpha: 0, x: () => window.innerWidth * 0.12 }, { autoAlpha: 1, x: 0, duration: 0.35, ease: 'power2.out' }, 0.7)

            fromToEach(gatherTimeline, q('[data-music-intro] [data-char]'), { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, duration: 0.3, ease: 'power3.out' }, 0.76, 0.035)

            const showScale = arts.map(art => gsap.quickSetter(art, 'scale') as (value: number) => void)
            const showOpacity = arts.map(art => gsap.quickSetter(art, 'opacity') as (value: number) => void)
            const captionOpacity = captions.map(caption => gsap.quickSetter(caption, 'opacity') as (value: number) => void)
            const captionY = captions.map(caption => gsap.quickSetter(caption, 'y', 'px') as (value: number) => void)

            let current = -1

            const updateActive = (progress: number): void => {
                const x = Number(gsap.getProperty(track, 'x')) || 0
                const center = window.innerWidth / 2
                let closest = 0
                let best = Number.POSITIVE_INFINITY

                jackets.forEach((jacket, index) => {
                    const offset = Math.abs(jacket.offsetLeft + jacket.offsetWidth / 2 + x - center)
                    const t = Math.min(1, offset / (window.innerWidth * 0.5))

                    showScale[index](1 - t * 0.14)
                    showOpacity[index](1 - t * 0.45)
                    captionOpacity[index](Math.max(0, 1 - t * 2.4))
                    captionY[index](t * 24)

                    if (offset < best) {
                        best = offset
                        closest = index
                    }
                })

                if (bar) bar.style.transform = `scaleX(${progress.toFixed(4)})`

                if (closest !== current && counter && jackets.length) {
                    current = closest
                    counter.textContent = `${pad(closest + 1)} / ${pad(jackets.length)}`
                }
            }

            gsap.to(track, {
                x: () => -distance(),
                ease: 'none',
                scrollTrigger: {
                    trigger: section,
                    start: () => `top+=${gatherLength()} top`,
                    end: () => `top+=${gatherLength() + distance()} top`,
                    scrub: 1,
                    invalidateOnRefresh: true,
                    onUpdate: self => updateActive(self.progress),
                    onRefresh: self => updateActive(self.progress)
                }
            })

            updateActive(0)

            return () => {
                ScrollTrigger.removeEventListener('refreshInit', setHeight)
                section.removeAttribute('data-enhanced')
                section.style.removeProperty('height')
            }
        })

        mm.add(MOTION_QUERIES.mobile, () => {
            gsap.timeline({ scrollTrigger: { trigger: section, start: 'top 75%' } })
                .from(q('[data-heading-meta]'), { autoAlpha: 0, x: -14, duration: 0.9, stagger: 0.1, ease: 'power2.out' })
                .from(q('[data-music-intro] [data-char]'), { autoAlpha: 0, y: 30, duration: 1.1, stagger: 0.07, ease: 'power3.out' }, 0)

            const items = q('[data-jacket]')

            if (items.length) {
                gsap.from(items, {
                    autoAlpha: 0,
                    x: 60,
                    duration: 1.3,
                    stagger: 0.12,
                    ease: 'power3.out',
                    scrollTrigger: { trigger: items[0], start: 'top 85%' }
                })
            }
        })
    }, { scope: sectionRef, dependencies: [tracks.length] })

    return (
        <section
            id='music'
            ref={sectionRef}
            className='group relative overflow-hidden bg-[linear-gradient(180deg,#f6f1e7_0%,#f4e5cd_45%,#efd6b8_100%)] data-[enhanced]:overflow-visible'
            style={{ '--jacket': 'min(46vh, 28vw)' } as CSSProperties}
        >
            <div
                data-music='stage'
                className='relative py-28 group-data-[enhanced]:sticky group-data-[enhanced]:top-0 group-data-[enhanced]:h-svh group-data-[enhanced]:overflow-hidden group-data-[enhanced]:py-0'
            >
                <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                    <div className='absolute -right-[10%] -top-[30%] aspect-square w-[70vw] rounded-full bg-[radial-gradient(closest-side,rgba(255,236,190,0.95),rgba(255,236,190,0))]' />
                    <div className='absolute -left-[20%] top-[20%] h-[160%] w-[38vw] -rotate-[24deg] bg-[linear-gradient(90deg,rgba(255,255,255,0),rgba(255,250,235,0.45),rgba(255,255,255,0))]' />
                    <div className='absolute left-[38%] top-[-30%] h-[160%] w-[18vw] -rotate-[24deg] bg-[linear-gradient(90deg,rgba(255,255,255,0),rgba(255,250,235,0.35),rgba(255,255,255,0))]' />
                    <div className='absolute inset-x-0 top-0 h-1/3 bg-[linear-gradient(180deg,rgba(231,225,238,0.55),rgba(231,225,238,0))]' />
                </div>

                <div data-music='gather' aria-hidden='true' className='pointer-events-none absolute inset-0 hidden group-data-[enhanced]:block'>
                    {GATHER_STARS.map((star, index) => (
                        <span
                            key={index}
                            data-gather-star
                            className='absolute block'
                            style={{ left: `${star.x}%`, top: `${star.y}%`, width: star.size, height: star.size }}
                        >
                            <Sparkle
                                className={cn(
                                    'block h-full w-full',
                                    star.gold
                                        ? 'text-gold drop-shadow-[0_0_6px_rgba(255,226,150,0.9)]'
                                        : 'text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.95)]'
                                )}
                            />
                        </span>
                    ))}

                    <div className='absolute inset-0 flex items-center justify-center'>
                        <div data-music='flash' className='relative flex size-[46vmin] items-center justify-center opacity-0'>
                            <span className='absolute inset-0 rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,1),rgba(255,244,214,0.75)_40%,rgba(255,244,214,0)_75%)]' />
                            <Sparkle className='relative size-[34%] text-gold drop-shadow-[0_0_24px_rgba(255,226,150,0.95)]' />
                        </div>
                    </div>
                </div>

                <div
                    data-music='content'
                    className='relative flex flex-col gap-10 group-data-[enhanced]:h-full group-data-[enhanced]:justify-center group-data-[enhanced]:gap-0 group-data-[enhanced]:pt-24'
                >
                    <div
                        data-music='row'
                        role='region'
                        aria-label='楽曲一覧'
                        tabIndex={0}
                        className='no-scrollbar snap-x snap-mandatory overflow-x-auto overscroll-x-contain focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-gold group-data-[enhanced]:snap-none group-data-[enhanced]:overflow-visible'
                    >
                        <ol data-music='track' className='relative flex w-max items-start gap-6 px-5 pb-4 lg:gap-[4.5vw] lg:px-[6vw]'>
                            <li data-music-intro className='flex w-[82vw] max-w-[380px] shrink-0 snap-start flex-col justify-center gap-6 group-data-[enhanced]:h-[var(--jacket)] group-data-[enhanced]:w-[30vw] group-data-[enhanced]:max-w-none'>
                                <SectionHeading
                                    index={MUSIC_COPY.index}
                                    kicker={MUSIC_COPY.kicker}
                                    title={MUSIC_COPY.title}
                                    ja={MUSIC_COPY.ja}
                                    titleClassName='lg:text-[clamp(3rem,7vw,7.5rem)]'
                                />
                                <p className='text-[15px] leading-[2] tracking-[0.05em] text-ink/80'>{MUSIC_COPY.lead}</p>
                                {tracks.length === 0 && (
                                    <p className='flex items-center gap-2 text-sm tracking-[0.08em] text-ink/70'>
                                        <Sparkle className='size-3 text-gold' />
                                        {MUSIC_COPY.empty}
                                    </p>
                                )}
                                {tracks.length > 0 && (
                                    <p className='hidden items-center gap-3 font-mono text-[10px] tracking-[0.32em] text-ink/60 group-data-[enhanced]:flex'>
                                        <span className='h-px w-10 bg-current' />
                                        SCROLL TO PLAY
                                    </p>
                                )}
                            </li>

                            {tracks.map((track, index) => (
                                <li
                                    key={track.id}
                                    data-jacket
                                    className='w-[78vw] max-w-[360px] shrink-0 snap-center group-data-[enhanced]:w-[var(--jacket)] group-data-[enhanced]:max-w-none'
                                >
                                    <JacketLink track={track}>
                                        <div
                                            data-jacket-art
                                            className='relative aspect-square overflow-hidden rounded-[6px] bg-ink/5 shadow-[0_40px_70px_-36px_rgba(29,42,51,0.6)] ring-1 ring-ink/10'
                                        >
                                            {track.cover
                                                ? (
                                                    <MediaImage
                                                        src={track.cover}
                                                        alt={track.coverAlt ?? `${track.title} のジャケット`}
                                                        sizes='(min-width: 1024px) 32vw, 78vw'
                                                        className='object-cover transition-transform duration-[1200ms] ease-[var(--ease-cinema)] group-hover/jacket:scale-[1.05]'
                                                    />
                                                )
                                                : <TrackJacket track={track} index={index} />}
                                            {track.listenUrl && (
                                                <span aria-hidden='true' className='absolute bottom-3 right-3 flex size-11 items-center justify-center rounded-full bg-mist/90 text-ink opacity-0 shadow-md ring-1 ring-ink/10 transition-opacity duration-500 group-hover/jacket:opacity-100 group-focus-visible/jacket:opacity-100'>
                                                    <Play className='size-4 translate-x-px' />
                                                </span>
                                            )}
                                        </div>
                                    </JacketLink>

                                    <div data-jacket-caption className='mt-5 flex flex-col gap-2'>
                                        <p className='flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] tracking-[0.22em] text-ink-soft'>
                                            <span>{pad(index + 1)}</span>
                                            {track.kind && (
                                                <span data-mdlfy-cms={`tracks:${track.id}:kind`} className='rounded-full px-2 py-0.5 text-ink ring-1 ring-ink/20'>
                                                    {track.kind}
                                                </span>
                                            )}
                                            {track.releaseLabel && <span>{track.releaseLabel}</span>}
                                        </p>
                                        <h3 data-mdlfy-cms={`tracks:${track.id}:title`} className='font-heading text-[1.75rem] leading-tight tracking-[0.04em] lg:text-[clamp(1.6rem,2.4vw,2.4rem)]'>
                                            {track.title}
                                        </h3>
                                        {track.description && (
                                            <p data-mdlfy-cms={`tracks:${track.id}:description`} className='text-sm leading-[1.9] tracking-[0.04em] text-ink/75'>
                                                {track.description}
                                            </p>
                                        )}
                                        {(track.listenUrl || track.streamUrl) && (
                                            <div className='mt-1 flex flex-wrap items-center gap-x-5 gap-y-2'>
                                                {track.listenUrl && (
                                                    <a href={track.listenUrl} target='_blank' rel='noopener noreferrer' className={LISTEN_LINK_CLASS}>
                                                        {MUSIC_COPY.listen}
                                                        <ArrowUpRight className='size-4' />
                                                        <span className='sr-only'>（{track.title}、新しいタブで開きます）</span>
                                                    </a>
                                                )}
                                                {track.streamUrl && (
                                                    <a href={track.streamUrl} target='_blank' rel='noopener noreferrer' className={LISTEN_LINK_CLASS}>
                                                        {MUSIC_COPY.stream}
                                                        <ArrowUpRight className='size-4' />
                                                        <span className='sr-only'>（{track.title}、新しいタブで開きます）</span>
                                                    </a>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </li>
                            ))}

                            {tracks.length > 0 && (
                                <li className='flex w-[70vw] max-w-[300px] shrink-0 snap-center flex-col justify-center gap-4 self-stretch group-data-[enhanced]:h-[var(--jacket)] group-data-[enhanced]:w-[22vw] group-data-[enhanced]:max-w-none group-data-[enhanced]:self-start'>
                                    <Sparkle className='size-8 text-gold' />
                                    <p className='font-heading text-4xl italic tracking-[0.04em]'>{MUSIC_COPY.endTitle}</p>
                                    <p className='text-sm leading-[1.9] tracking-[0.06em] text-ink/70'>{MUSIC_COPY.endText}</p>
                                    {siteConfig.links.youtube && (
                                        <a href={siteConfig.links.youtube} target='_blank' rel='noopener noreferrer' className={LISTEN_LINK_CLASS}>
                                            <Youtube className='size-4' />
                                            {MUSIC_COPY.endCta}
                                            <ArrowUpRight className='size-4' />
                                            <span className='sr-only'>（新しいタブで開きます）</span>
                                        </a>
                                    )}
                                </li>
                            )}
                        </ol>
                    </div>

                    {tracks.length > 0 && (
                        <div aria-hidden='true' className='pointer-events-none absolute bottom-[6vh] left-[6vw] right-[6vw] hidden items-center gap-6 font-mono text-[10px] tracking-[0.3em] text-ink/70 group-data-[enhanced]:flex'>
                            <span className='flex items-center gap-2'>
                                <Sparkle className='size-2.5 text-gold' />
                                NOW PLAYING
                            </span>
                            <span data-music-counter>{`01 / ${pad(tracks.length)}`}</span>
                            <span className='relative h-px flex-1 bg-ink/15'>
                                <span data-music-bar className='absolute inset-0 origin-left bg-ink/60' style={{ transform: 'scaleX(0)' }} />
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </section>
    )
}

export default MusicSection
